import { FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { FEED_SORTS, FeedCompanyJumpEntry, FeedSort } from "@signalist/shared-types";

import { Icon } from "@/components";
import { Spinner } from "@/components/Spinner/Spinner";
import { classNames } from "@/utils/classNames";

import { AnchoredPopover } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/AnchoredPopover";

import { feedIdentity } from "../utils/feedCardKey";

const PANEL_WIDTH = 420;

const SORT_LABEL_KEYS: Record<FeedSort, string> = {
	strength: "feed.jump.sortStrength",
	date: "feed.jump.sortDate",
};

/** Company logo of a jump entry — a broken URL falls back to the initial. */
const JumpEntryLogo: FunctionComponent<{ entry: FeedCompanyJumpEntry }> = ({ entry }) => {
	const [hasLogoError, setHasLogoError] = useState(false);

	return (
		<span className="flex items-center justify-center w-6 h-6 shrink-0 rounded-md bg-inactive-item text-[11px] font-semibold text-text-meta overflow-hidden">
			{entry.logoUrl && !hasLogoError ? (
				<img
					alt={entry.name ?? ""}
					className="w-full h-full object-cover"
					src={entry.logoUrl}
					onError={() => setHasLogoError(true)}
				/>
			) : (
				(entry.name ?? "?").charAt(0).toUpperCase()
			)}
		</span>
	);
};

export interface JumpToCompanyDropdownProps {
	companies: FeedCompanyJumpEntry[];
	isLoading: boolean;
	sort: FeedSort;
	onSortChange: (sort: FeedSort) => void;
	/** Jumps to the clicked company's card — its `page` pages there, its identity is the target (D3). */
	onJump: (entry: FeedCompanyJumpEntry) => void;
	/** Lets the page shell enable the `/feed/companies` query on first open. */
	onOpenChange?: (open: boolean) => void;
	/** Extension escape-hatch (web rule §11) — panel portals into `portalContainer ?? document.body`. */
	portalContainer?: HTMLElement | null;
}

/**
 * Jump-to-company chevron next to the New tab (D3): a portaled panel listing
 * the ordered companies of the current tab with a strength/date sort. Clicking
 * an entry hands it to the page shell, which pages to it, then selects, scrolls
 * to and flashes its card.
 */
export const JumpToCompanyDropdown: FunctionComponent<JumpToCompanyDropdownProps> = ({
	companies,
	isLoading,
	sort,
	onSortChange,
	onJump,
	onOpenChange,
	portalContainer,
}) => {
	const { t } = useTranslation();

	const triggerRef = useRef<HTMLButtonElement>(null);
	const [isOpen, setIsOpen] = useState(false);

	const setOpen = (open: boolean) => {
		setIsOpen(open);
		onOpenChange?.(open);
	};

	return (
		<>
			<button
				ref={triggerRef}
				className="flex items-center justify-center w-6 h-[32px] -ml-1 mr-1 text-text-meta hover:text-text cursor-pointer"
				data-testid="feed-jump-trigger"
				title={t("feed.jump.trigger")}
				type="button"
				onClick={() => setOpen(!isOpen)}
			>
				<Icon className="w-3 h-3" icon={faChevronDown} />
			</button>

			{isOpen && (
				<AnchoredPopover
					align="left"
					anchorEl={triggerRef.current}
					portalContainer={portalContainer}
					width={PANEL_WIDTH}
					onClose={() => setOpen(false)}
				>
					<div
						className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
						data-testid="feed-jump-panel"
					>
						<div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border-light">
							<span className="text-xs font-semibold text-text whitespace-nowrap">
								{t("feed.jump.title")}
							</span>
							<div className="flex items-center gap-1">
								<span className="text-[11px] text-text-meta whitespace-nowrap">
									{t("feed.jump.sortBy")}
								</span>
								{FEED_SORTS.map((sortOption) => (
									<button
										key={sortOption}
										className={classNames(
											"px-1.5 py-0.5 text-[11px] font-medium rounded-md cursor-pointer duration-150 whitespace-nowrap",
											sort === sortOption
												? "bg-inactive-item text-blue-action"
												: "text-text-meta hover:text-text"
										)}
										data-testid={`feed-jump-sort-${sortOption}`}
										type="button"
										onClick={() => onSortChange(sortOption)}
									>
										{t(SORT_LABEL_KEYS[sortOption])}
									</button>
								))}
							</div>
						</div>

						<div className="bb-scrollbar-darker max-h-[320px] overflow-y-auto">
							{isLoading ? (
								<div className="flex items-center justify-center py-4">
									<Spinner color="#4d66f5" />
								</div>
							) : companies.length > 0 ? (
								companies.map((entry) => (
									<button
										key={feedIdentity(entry.runId, entry.trackerOrganizationId)}
										className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-gray-100 duration-150 cursor-pointer"
										data-testid={`feed-jump-entry-${feedIdentity(
											entry.runId,
											entry.trackerOrganizationId
										)}`}
										type="button"
										onClick={() => {
											onJump(entry);
											setOpen(false);
										}}
									>
										<JumpEntryLogo entry={entry} />
										<span className="flex flex-col min-w-0">
											<span className="text-xs font-semibold text-text truncate">{entry.name}</span>
											{entry.topSignalLine && (
												<span className="text-[11px] text-text-meta truncate">
													{entry.topSignalKind ? `${entry.topSignalKind} · ` : ""}
													{entry.topSignalLine}
												</span>
											)}
										</span>
									</button>
								))
							) : (
								<p className="px-3 py-2 text-sm italic text-gray-500">{t("feed.jump.empty")}</p>
							)}
						</div>
					</div>
				</AnchoredPopover>
			)}
		</>
	);
};
